/**
 * Welcome / Onboarding screen — shown on fresh install (no gateways configured).
 *
 * Two paths:
 * - "Sign In" → magic link auth flow (reuses Login.tsx)
 * - "Connect Directly" → gateway URL input (no auth needed)
 *
 * Mobile-first, polished, dark theme.
 */

import { useState, useCallback } from 'react';
import { reloadApp } from '../utils/reload';
import styles from './Welcome.module.css';

type WelcomeScreen = 'welcome' | 'connect';

interface WelcomeProps {
  /** Called when user wants to sign in (triggers Login flow) */
  onSignIn: () => void;
  /** Called when user submits gateway URL + token to connect directly */
  onConnect: (url: string, token: string, name: string) => Promise<void>;
  /** Whether the user is already authenticated (e.g. via magic link) */
  isAuthenticated?: boolean;
  /** Email of the authenticated user (shown as status indicator) */
  userEmail?: string;
}

/**
 * Validate a gateway URL.
 * Must start with ws:// or wss:// and be a valid URL.
 */
function validateGatewayUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return 'Gateway URL is required';
  if (!trimmed.startsWith('ws://') && !trimmed.startsWith('wss://')) {
    return 'URL must start with ws:// or wss://';
  }
  try {
    new URL(trimmed.replace(/^ws/, 'http'));
  } catch {
    return 'Invalid URL format';
  }
  return null;
}

export function Welcome({ onSignIn, onConnect, isAuthenticated, userEmail }: WelcomeProps) {
  // When authenticated, skip the welcome choice and go straight to connect
  const [screen, setScreen] = useState<WelcomeScreen>(isAuthenticated ? 'connect' : 'welcome');
  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate URL
    const urlError = validateGatewayUrl(url);
    if (urlError) {
      setError(urlError);
      return;
    }

    // Validate token
    if (!token.trim()) {
      setError('Gateway token is required');
      return;
    }

    setIsConnecting(true);
    try {
      await onConnect(url.trim(), token.trim(), name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed. Please check your URL and token.');
    } finally {
      setIsConnecting(false);
    }
  }, [url, token, name, onConnect]);

  // ── Welcome screen (two CTA buttons) ──
  if (screen === 'welcome') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.logo}>🔥</div>
          <h1 className={styles.title}>Talkyn</h1>
          <p className={styles.tagline}>Your AI voice assistant</p>

          <div className={styles.actions}>
            {isAuthenticated ? (
              <>
                <div className={styles.authStatus}>
                  ✅ Signed in{userEmail ? ` as ${userEmail}` : ''}
                </div>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => setScreen('connect')}
                >
                  Connect to Gateway
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={onSignIn}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setScreen('connect')}
                >
                  Connect Directly
                </button>
              </>
            )}
          </div>

          <p className={styles.footer}>
            {isAuthenticated
              ? 'Connect your OpenClaw gateway to start using Talkyn'
              : 'By signing in, you get cloud sync, file storage, and more'}
          </p>
        </div>
      </div>
    );
  }

  // ── Connect directly screen (gateway URL input) ──
  return (
    <div className={styles.container}>
      <div className={styles.inputScreen}>
        {!isAuthenticated && (
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              setScreen('welcome');
              setError(null);
            }}
          >
            ← Back
          </button>
        )}

        {isAuthenticated && (
          <div className={styles.authBanner}>
            ✅ Signed in{userEmail ? ` as ${userEmail}` : ''}
          </div>
        )}

        <h2 className={styles.inputTitle}>Connect to Gateway</h2>
        <p className={styles.inputSubtitle}>
          Enter your OpenClaw gateway details to start
        </p>

        <form onSubmit={handleConnect} className={styles.form}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Gateway URL</label>
            <input
              type="text"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
              }}
              placeholder="wss://your-gateway.example.com"
              className={`${styles.input} ${error && !url.trim() ? styles.inputError : ''}`}
              autoComplete="url"
              spellCheck={false}
              autoFocus
              disabled={isConnecting}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Gateway Token</label>
            <input
              type="password"
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setError(null);
              }}
              placeholder="Your gateway token"
              className={`${styles.input} ${error && !token.trim() ? styles.inputError : ''}`}
              autoComplete="off"
              disabled={isConnecting}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Gateway"
              className={styles.nameInput}
              disabled={isConnecting}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <button
            type="submit"
            className={styles.connectBtn}
            disabled={isConnecting || !url.trim() || !token.trim()}
          >
            {isConnecting ? 'Connecting…' : 'Connect'}
          </button>
        </form>

        <div className={styles.utilityRow}>
          <button
            type="button"
            className={styles.utilityBtn}
            onClick={() => reloadApp()}
          >
            🔄 Reload
          </button>
          <button
            type="button"
            className={styles.utilityBtn}
            onClick={() => {
              if (confirm('Clear all app data and reload?')) {
                localStorage.clear();
                reloadApp();
              }
            }}
          >
            🗑️ Reset
          </button>
        </div>
      </div>
    </div>
  );
}
